import React, { useMemo, memo } from "react";
import { getInputFromDOM, getLocalizedValue } from "../util";
import { CompactFieldLabel } from "./components/CompactFieldLabel";
import _get from "lodash/get";
import { FieldLabel } from "react-invenio-forms";
import _deburr from "lodash/deburr";
import _escapeRegExp from "lodash/escapeRegExp";
import _filter from "lodash/filter";
import _isObject from "lodash/isObject";
import _isDate from "lodash/isDate";
import _isRegExp from "lodash/isRegExp";
import _forOwn from "lodash/forOwn";
import _isEmpty from "lodash/isEmpty";

/**
 * Merges model field data with component prop overrides.
 * Only overrides values that are explicitly provided (not undefined).
 * This allows components to override model defaults including with falsy values like `false`.
 *
 * @param {Object} modelData - The field data from getFieldData (model defaults)
 * @param {Object} overrides - Component props that should override model defaults
 * @returns {Object} Merged field data with overrides taking precedence
 *
 * @example
 * const fieldData = mergeFieldData(
 *   getFieldData({ fieldPath, icon, fieldRepresentation: "text" }),
 *   { required, label, helpText, placeholder }
 * );
 */
export const mergeFieldData = (modelData, overrides) => {
  const result = { ...modelData };
  for (const [key, value] of Object.entries(overrides)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
};

export function parseFormAppConfig(rootElementId = "deposit-form") {
  const rootEl = document.getElementById(rootElementId);

  return {
    rootEl,
    record: getInputFromDOM("deposits-record"),
    preselectedCommunity: getInputFromDOM("deposits-draft-community"),
    files: getInputFromDOM("deposits-record-files"),
    config: getInputFromDOM("deposits-config"),
    useUppy: getInputFromDOM("deposits-use-uppy-ui"),
    permissions: getInputFromDOM("deposits-record-permissions"),
    filesLocked: getInputFromDOM("deposits-record-locked-files"),
    recordRestrictionGracePeriod: getInputFromDOM(
      "deposits-record-restriction-grace-period"
    ),
    allowRecordRestriction: getInputFromDOM(
      "deposits-allow-record-restriction"
    ),
    recordDeletion: getInputFromDOM("deposits-record-deletion"),
    groupsEnabled: getInputFromDOM("config-groups-enabled"),
    allowEmptyFiles: getInputFromDOM("records-resources-allow-empty-files"),
    isDoiRequired: getInputFromDOM("deposits-is-doi-required"),
    fileModification: getInputFromDOM("deposits-file-modification"),
    links: getInputFromDOM("deposits-links"),
  };
}

const MemoizedFieldLabel = memo(FieldLabel);
const MemoizedCompactFieldLabel = memo(CompactFieldLabel);

export const getFieldData = (uiMetadata, fieldPathPrefix = "") => {
  return ({
    fieldPath,
    icon = "pencil",
    fullLabelClassName,
    compactLabelClassName,
    fieldRepresentation = "full",
    // escape hatch that allows you to use top most provider and provide full paths inside of deeply nested fields
    ignorePrefix = false,
  }) => {
    const fieldPathWithPrefix =
      fieldPathPrefix && !ignorePrefix
        ? `${fieldPathPrefix}.${fieldPath}`
        : fieldPath;
    // Handling labels, always taking result of i18next.t; if we get metadata/smth, we use it to debug
    // Help and hint: if result is same as the key, don't render; if it is different, render
    const path = toModelPath(fieldPathWithPrefix);

    const {
      help: modelHelp = undefined,
      label: modelLabel = undefined,
      hint: modelHint = undefined,
      required = undefined,
      detail = undefined,
    } = _get(uiMetadata, path) || {};

    const label = modelLabel ? getLocalizedValue(modelLabel) : path;
    const help = modelHelp ? getLocalizedValue(modelHelp) : null;
    const hint = modelHint ? getLocalizedValue(modelHint) : null;

    const memoizedResult = useMemo(() => {
      switch (fieldRepresentation) {
        case "full":
          return {
            helpText: help,
            label: (
              <MemoizedFieldLabel
                htmlFor={fieldPath}
                icon={icon}
                label={label}
                className={fullLabelClassName}
              />
            ),
            placeholder: hint,
            required,
            detail,
          };
        case "compact":
          return {
            label: (
              <MemoizedCompactFieldLabel
                htmlFor={fieldPath}
                icon={icon}
                label={label}
                popupHelpText={help}
                className={compactLabelClassName}
              />
            ),
            placeholder: hint,
            required,
            detail,
          };
        case "text":
          return {
            helpText: help,
            label: label,
            placeholder: hint,
            labelIcon: icon,
            required,
            detail,
          };
        default:
          throw new Error(
            `Unknown fieldRepresentation: ${fieldRepresentation}`
          );
      }
    }, [
      fieldPath,
      icon,
      label,
      help,
      hint,
      required,
      fieldRepresentation,
      fullLabelClassName,
      compactLabelClassName,
      detail,
    ]);

    return memoizedResult;
  };
};

export function toModelPath(path) {
  // Split the path into components
  const parts = path.split(".");

  const transformedParts = parts.map((part, index, array) => {
    if (index === 0) {
      return `children.${part}.children`;
    } else if (index === array.length - 1) {
      return part;
    } else if (!Number.isNaN(Number.parseInt(part))) {
      return `child.children`;
    } else if (Number.isNaN(Number.parseInt(array[index + 1]))) {
      return `${part}.children`;
    } else {
      return part;
    }
  });
  // Join the transformed parts back into a single string
  return transformedParts.join(".");
}

export const getValidTagsForEditor = (tags = [], attr = {}) => {
  const specialAttributes = Object.fromEntries(
    Object.entries(attr).map(([key, value]) => [key, value.join("|")])
  );
  let result = [];

  if (specialAttributes["*"]) {
    result.push(`@[${specialAttributes["*"]}]`);
  }

  result = result.concat(
    tags.map((tag) => {
      return specialAttributes[tag] ? `${tag}[${specialAttributes[tag]}]` : tag;
    })
  );

  return result.join(",");
};

// custom search function to avoid the issue of not being able to search
// through text in react nodes that are our dropdown options
// requires also name to be returned in serializer which is actually a text
// value
export const search = (filteredOptions, searchQuery, searchKey = "name") => {
  const strippedQuery = _deburr(searchQuery);

  const re = new RegExp(_escapeRegExp(strippedQuery), "i");

  filteredOptions = _filter(filteredOptions, (opt) =>
    re.test(_deburr(opt[searchKey]))
  );
  return filteredOptions;
};

/**
 * Finds the index of the form section that contains a given field path.
 *
 * @param {Array<{key: string, includesPaths?: string[]}>} sections - Array of section objects with includesPaths
 * @param {string} fieldPath - The field path to search for (e.g., "metadata.title" or "metadata.creators.0.name")
 * @returns {number} The index of the matching section, or -1 if not found
 *
 * @example
 * const sections = [
 *   { key: "basic", includesPaths: ["metadata.title", "metadata.description"] },
 *   { key: "creators", includesPaths: ["metadata.creators"] }
 * ];
 * findSectionIndexForFieldPath(sections, "metadata.creators.0.name"); // returns 1
 */
export const findSectionIndexForFieldPath = (sections, fieldPath) => {
  if (!sections || !fieldPath) return -1;
  return sections.findIndex((section) =>
    section.includesPaths?.some(
      (path) => fieldPath === path || fieldPath.startsWith(`${path}.`)
    )
  );
};

/**
 * Checks if an object is an error leaf node in the new error format.
 * New format errors have `message`, `severity`, and `description` properties.
 *
 * @param {*} obj - The value to check
 * @returns {boolean} True if the object is a new format error object
 *
 * @example
 * // New format error object
 * isErrorObject({ message: "Required", severity: "error", description: "Field must not be empty" }); // true
 *
 * // Old format (plain string)
 * isErrorObject("Required"); // false
 */
export const isErrorObject = (obj) =>
  _isObject(obj) &&
  "message" in obj &&
  "severity" in obj &&
  "description" in obj;

/**
 * Flattens a nested error object into an array of { fieldPath, value } pairs.
 * Handles both old format (string values) and new format (objects with message/severity/description).
 *
 * @param {Object} obj - The nested error object to flatten
 * @param {string} [prefix=""] - Internal parameter for building field paths during recursion
 * @param {Array} [res=[]] - Internal parameter for accumulating results during recursion
 * @returns {Array<{fieldPath: string, value: string|Object}>} Array of flattened errors
 *
 * @example
 * // Old format (string errors)
 * flattenToPathValueArray({ metadata: { title: "Required" } });
 * // Returns: [{ fieldPath: "metadata.title", value: "Required" }]
 *
 * @example
 * // New format (object errors)
 * flattenToPathValueArray({
 *   metadata: {
 *     title: { message: "Required", severity: "error", description: "Must provide title" }
 *   }
 * });
 * // Returns: [{ fieldPath: "metadata.title", value: { message: "Required", severity: "error", description: "Must provide title" } }]
 */
export function flattenToPathValueArray(obj, prefix = "", res = []) {
  if (
    _isObject(obj) &&
    !_isDate(obj) &&
    !_isRegExp(obj) &&
    obj !== null &&
    !isErrorObject(obj)
  ) {
    _forOwn(obj, (value, key) => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      flattenToPathValueArray(value, newKey, res);
    });
  } else if (obj !== undefined) {
    // Skip undefined leaves: lodash's `_forOwn` iterates arrays from 0 to length-1,
    // so a sparse hole (e.g. from `_set(obj, "a.1.b", "x")` leaving index 0 unset)
    // reads as `undefined` and would otherwise emit a phantom entry.
    res.push({ fieldPath: prefix, value: obj });
  }
  return res;
}

/**
 * Recursively traverses an object to find all errors (both old and new format).
 * Old format: plain strings
 * New format: objects with message, severity, description
 *
 * @param {Object} obj - The object to traverse
 * @returns {Array} Array of errors found (strings or error objects)
 *
 * @example
 * // New format
 * findErrorObjects({ field: { message: "Required", severity: "error", description: "..." } });
 * // Returns: [{ message: "Required", severity: "error", description: "..." }]
 *
 * @example
 * // Old format
 * findErrorObjects({ field: "Required" });
 * // Returns: ["Required"]
 */
export function findErrorObjects(obj) {
  const results = [];
  function traverse(current) {
    if (current === null || current === undefined) return;

    // New format error object
    if (isErrorObject(current)) {
      results.push(current);
      return;
    }

    // Old format string error
    if (typeof current === "string") {
      results.push(current);
      return;
    }

    // Array or object - traverse children
    if (typeof current === "object") {
      if (Array.isArray(current)) {
        for (const item of current) {
          traverse(item);
        }
      } else {
        for (const key of Object.keys(current)) {
          traverse(current[key]);
        }
      }
    }
  }
  traverse(obj);
  return results;
}

/**
 * Gets all errors for specified field paths from errors and initialErrors objects.
 *
 * @param {Object} errors - Formik errors object
 * @param {Object} initialErrors - Formik initialErrors object
 * @param {string[]} includesPaths - Array of field paths to check for errors
 * @returns {Array} Array of error objects or error strings
 *
 * @example
 * getSubfieldErrors({ metadata: { title: { message: "Required", severity: "error", description: "..." } } }, {}, ["metadata.title"]);
 */
export function getSubfieldErrors(errors, initialErrors, includesPaths = []) {
  const subfieldErrors = [];
  for (const fieldPath of includesPaths) {
    const err = _get(errors, fieldPath) || _get(initialErrors, fieldPath);
    if (err) {
      if (isErrorObject(err)) {
        // New format error object directly at this path
        subfieldErrors.push(err);
      } else if (typeof err === "string") {
        // Old format string error directly at this path
        subfieldErrors.push(err);
      } else if (typeof err === "object") {
        // Nested structure - find all errors within (both old and new format)
        const errs = findErrorObjects(err);
        subfieldErrors.push(...errs);
      }
    }
  }
  return subfieldErrors;
}

/**
 * Categorizes errors by severity into info, warning, and error buckets.
 *
 * @param {Array} errors - Array of error objects or strings
 * @returns {{info: Array, warning: Array, error: Array}} Categorized errors
 *
 * @example
 * categorizeErrors([{ message: "Required", severity: "error" }, "Old format error"]);
 * // Returns: { info: [], warning: [], error: [{ message: "Required", severity: "error" }, "Old format error"] }
 */
export function categorizeErrors(errors) {
  const categories = { info: [], warning: [], error: [] };
  for (const err of errors) {
    if (!Object.hasOwn(err, "severity")) {
      categories.error.push(err);
    } else {
      categories[`${err.severity}`].push(err);
    }
  }
  return categories;
}

/**
 * Checks if a plain object has any meaningful filled values,
 * ignoring internal keys like __key.
 */
export const isObjectFilled = (obj) => {
  return Object.entries(obj).some(
    ([key, val]) => !key.startsWith("__") && isFilled(val)
  );
};

/**
 * Checks if a single value is considered "filled".
 * Handles booleans and numbers as always filled (if defined).
 * For arrays, filters out items that are empty objects (e.g. skeleton entries
 * with only internal keys like __key).
 * For plain objects, checks if any non-internal key has a filled value.
 *
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is considered filled
 */
export const isFilled = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean" || typeof value === "number") return true;
  if (Array.isArray(value)) {
    return value.some((item) => isFilled(item));
  }
  if (_isObject(value)) {
    return isObjectFilled(value);
  }
  return !_isEmpty(value);
};

/**
 * Checks if the files section has any file entries in the Redux state.
 *
 * @param {Object} params
 * @param {Object} params.reduxState - Redux store state containing files.entries
 * @returns {number} 1 if there is at least one file entry, 0 otherwise
 */
export const computeFilesSectionCompletion = ({ reduxState }) => {
  const entries = _get(reduxState, "files.entries", {});
  return Object.keys(entries).length > 0 ? 1 : 0;
};

/**
 * Computes how filled a section is based on its includesPaths.
 * Returns a number between 0 and 1 representing the fraction of filled fields.
 *
 * @param {Object} params
 * @param {Object} params.formikValues - Formik values object
 * @param {string[]} params.includesPaths - Array of field paths to check
 * @returns {number} Fraction filled (0 to 1)
 */
export const computeSectionCompletion = ({ formikValues, includesPaths }) => {
  if (!includesPaths || includesPaths.length === 0) return 1;

  let filledCount = 0;
  for (const path of includesPaths) {
    const formikValue = _get(formikValues, path);
    if (isFilled(formikValue)) {
      filledCount++;
    }
  }
  return filledCount / includesPaths.length;
};
